import { createApp, h } from 'vue';

import { uicomponents } from 'uicomponents';
import { TV_WINDOW_NAME } from '@/constants';
import { getStore, storeKey } from '@/utils/storeAccess';
import { getRouter } from '@/utils/routerAccess';
import i18n from '@/i18n';

let visiblePopUps = [];

function getPopUpId(props) {
  const componentId = props?.props?.id;

  if (props?.message || componentId) {
    const otherProps = ['messages', 'title', 'message'];

    let popUpId = otherProps.reduce((acc, prop) => {
      return acc + (props[prop] || '');
    }, '');

    if (componentId) {
      popUpId = componentId;
    }

    return popUpId;
  }

  return null;
}

export function mount(el, component, props, options = {}) {
  let app;
  let mountedComponent;
  let closed = false;
  let closeRequested = false;
  let dialogExitCompleted = false;
  let activeCallbackName = null;
  let suppressRequestedCloseCancelCallback = false;
  let selectedActionCallbackName = null;
  let selectedActionCallbackInvoked = false;
  const pendingCallbackNames = new Set();
  const actionCallbackNames = new Set(['onCancel', 'onConfirm', 'onHide']);
  const selectActionCallback = callbackName => {
    if (!actionCallbackNames.has(callbackName)) {
      return true;
    }
    if (!selectedActionCallbackName) {
      selectedActionCallbackName = callbackName;
    }
    return selectedActionCallbackName === callbackName;
  };
  const shouldInvokeActionCallback = callbackName => {
    if (!actionCallbackNames.has(callbackName)) {
      return true;
    }
    if (!selectActionCallback(callbackName) || selectedActionCallbackInvoked) {
      return false;
    }
    selectedActionCallbackInvoked = true;
    return true;
  };
  const store = getStore();
  const promise = new Promise((resolve, reject) => {
    app = createApp({
      name: 'service-mountstage',
      methods: {
        closechild() {
          if (this.$refs.mountedComponent?.close) {
            this.$refs.mountedComponent.close();
          }
        },
        requestClose(reason) {
          if (this.$refs.mountedComponent?.requestClose) {
            this.$refs.mountedComponent.requestClose(reason);
            return;
          }
          if (this.$refs.mountedComponent?.cancel) {
            this.$refs.mountedComponent.cancel(reason);
            return;
          }
          close();
        }
      },
      render() {
        const popUpId = getPopUpId(props);

        if (popUpId) {
          if (visiblePopUps.indexOf(popUpId) > -1) {
            return;
          }
          visiblePopUps.push(popUpId);
        }

        const injectedOns = Object.keys(options.on || {}).reduce((accum, cbname) => {
          const callback = options.on[cbname];
          accum[cbname] = reason => {
            if (cbname === 'onCancel' || cbname === 'onHide') {
              dialogExitCompleted = true;
              if (closeRequested) {
                close();
              }
            }
            if (options.closeOnCTAClick) {
              close();
            }
            if (!shouldInvokeActionCallback(cbname)) {
              return;
            }
            if (cbname === 'onCancel' && suppressRequestedCloseCancelCallback) {
              suppressRequestedCloseCancelCallback = false;
              return;
            }

            activeCallbackName = cbname;
            let callbackResult;
            try {
              callbackResult = callback(reason);
            } catch (error) {
              reject(error);
              return;
            } finally {
              activeCallbackName = null;
            }
            if (callbackResult?.then) {
              pendingCallbackNames.add(cbname);
              Promise.resolve(callbackResult)
                .then(resolve, reject)
                .finally(() => pendingCallbackNames.delete(cbname));
              return;
            }
            resolve(callbackResult);
          };
          return accum;
        }, {});
        const on = {
          onHide(reason) {
            dialogExitCompleted = true;
            close();
            resolve(reason);
          },
          onCancel(reason) {
            dialogExitCompleted = true;
            close();
            if (reason) {
              reject(reason);
            }
          },
          ...injectedOns
        };
        const optionsWithoutOn = {
          ...options
        };
        optionsWithoutOn.on = undefined;
        return h(component, {
          ref: 'mountedComponent',
          ...props,
          props: {
            ...on,
            onCancel: undefined,
            onHide: undefined,
            ...props.props
          },
          ...on,
          ...optionsWithoutOn,
          shouldHandleAction: selectActionCallback
        });
      }
    });
    app.config.globalProperties.$router = getRouter();
    app.config.globalProperties.$store = store;
    app.config.globalProperties.$t = i18n.global.t;
    app.use(uicomponents, {
      $trackEvent: () => {}
    });
    if (store) {
      app.use(store, storeKey);
    }
    app.mixin({
      $t: i18n.global.t,
      $i18n: i18n
    });

    mountedComponent = app.use(i18n).mount(el);
  });

  function close(closechild = false) {
    if (closed) {
      return;
    }
    closed = true;
    const popUpId = getPopUpId(props);

    if (popUpId) {
      visiblePopUps.splice(visiblePopUps.indexOf(popUpId), 1);
    }

    if (closechild) {
      app.closechild();
    } else {
      app.unmount();
      el.remove();
    }
    options.onClosed?.();
  }

  function requestClose(reason) {
    if (dialogExitCompleted) {
      close();
      return;
    }

    if (activeCallbackName === 'onConfirm' || (!activeCallbackName && pendingCallbackNames.has('onConfirm'))) {
      suppressRequestedCloseCancelCallback = true;
    }
    closeRequested = true;
    if (typeof mountedComponent?.requestClose === 'function') {
      mountedComponent.requestClose(reason);
      return;
    }
    close();
  }

  return { promise, ref: app, close, requestClose };
}

export function openTvWindow(url) {
  let params = 'width=' + screen.width;
  params += ', height=' + screen.height;
  params += ', top=0, left=0';
  params += ', fullscreen=yes';
  params += ', directories=no';
  params += ', location=no';
  params += ', menubar=no';
  params += ', resizable=no';
  params += ', scrollbars=no';
  params += ', status=no';
  params += ', popup=yes';
  params += ', toolbar=no';

  const tvWindow = window.open(url, TV_WINDOW_NAME, params);
  if (window.focus) {
    tvWindow.focus();
  }

  watchTvWindow(tvWindow);
  return tvWindow;
}

export function watchTvWindow(tvWindow) {
  let closingInterval = null;
  let tries = 0;

  const checkClosed = () => {
    if (tvWindow.closed) {
      getStore()?.commit('event/stage/setTvWindow', undefined);
      clearInterval(closingInterval);
    } else if (tries > 2) {
      tries = 0;
      clearInterval(closingInterval);
    } else {
      tries++;
    }
  };

  tvWindow.onunload = () => {
    clearInterval(closingInterval);
    closingInterval = setInterval(checkClosed, 1000);
  };
}
